import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

/**
* NotificationPopup Component
*
* A notification popup that remains visible until dismissed by the user.
* Contains a dynamic message and a dynamic action button.
*
* @param {Object} props
* @param {boolean} props.isOpen - Controls visibility of the popup
* @param {function} props.onClose - Function to call when closing the popup
* @param {string} props.message - The notification message to display (fallback)
* @param {string} props.actionText - The text for the action button (e.g., "View Application")
* @param {function} props.onAction - Function to call when the action button is clicked
* @param {Array} props.statusData - Status data from the backend API
* @param {Array} props.elections - Elections data array
*/
const NotificationPopup = ({
  isOpen,
  onClose,
  message = "You have a new notification",
  actionText = "View Details",
  onAction = () => {},
  statusData = [],
  elections = []
}) => {
  const [dynamicMessage, setDynamicMessage] = useState(message);
  const [dynamicNavTarget, setDynamicNavTarget] = useState('assignments-section');
  const [shouldShowPopup, setShouldShowPopup] = useState(true);
  const [isExternalLink, setIsExternalLink] = useState(false);
  const [externalLinkUrl, setExternalLinkUrl] = useState('');
  const [buttonText, setButtonText] = useState(actionText);
  const [isClosing, setIsClosing] = useState(false);
  
  // Find the correct status object based on election_status
  useEffect(() => {
    // Check if there are any elections
    if (!elections || elections.length === 0) {
      console.log('No election records found, hiding popup');
      setShouldShowPopup(false);
      return;
    }
    // Check if there are any DJ elections within the election record
    const djElection = elections[0]?.dj_elections?.[0];
    if (!djElection) {
      console.log('No dj-election data found for the current election, hiding popup');
      setShouldShowPopup(false);
      return;
    }
    if (isOpen && Array.isArray(statusData) && elections && elections.length > 0) {
      // console.log('Status data in NotificationPopup:', statusData);
      // console.log('Elections data in NotificationPopup:', elections);
      // Get the election_status from the first election's dj_elections array
      const electionStatus = djElection.election_status;
      // console.log('Current election status:', electionStatus);
      // Check if statusData is an array before using find()
      if (Array.isArray(statusData)) {
        // Find the matching status object
        const foundStatus = statusData.find(status =>
          status.status === electionStatus
        );
        if (foundStatus) {
          // Set dynamic message and navigation target if available
          if (foundStatus.popup_message && foundStatus.popup_message.trim() !== '') {
            setDynamicMessage(foundStatus.popup_message);
          }
          
          // Check for external link
          if (foundStatus.external_link && foundStatus.external_link.trim() !== '') {
            setIsExternalLink(true);
            setExternalLinkUrl(foundStatus.external_link);
            setShouldShowPopup(true);
          } else {
            setIsExternalLink(false);
            setExternalLinkUrl('');
          }
          
          // Set button text if available
          if (foundStatus.btn_text && foundStatus.btn_text.trim() !== '') {
            setButtonText(foundStatus.btn_text);
          } else {
            // Default button text based on link type
            setButtonText(isExternalLink ? "Open Link" : actionText);
          }
          
          // Check if popup_nav is defined and not empty
          if (!foundStatus.popup_nav || foundStatus.popup_nav.trim() === '') {
            console.log('popup_nav is undefined or empty');
            // Only hide popup if there's no external link either
            if (!isExternalLink) {
              console.log('No navigation target or external link, hiding popup');
              setShouldShowPopup(false);
            }
          } else {
            setDynamicNavTarget(foundStatus.popup_nav);
            setShouldShowPopup(true);
          }
        } else {
          console.log('No matching status found for election status:', electionStatus);
        }
      } else {
        console.log('statusData is not an array:', typeof statusData, statusData);
      }
    }
  }, [isOpen, statusData, elections, message, actionText, isExternalLink]);
  
  // Reset isClosing when popup is reopened
  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
    }
  }, [isOpen]);
  
  if (!isOpen || !shouldShowPopup) return null;
  
  // Custom action handler that uses the dynamic navigation target or external link
  const handleAction = () => {
    onClose(); // Close the popup
    
    if (isExternalLink && externalLinkUrl) {
      // Open external link in a new tab
      window.open(externalLinkUrl, '_blank', 'noopener,noreferrer');
    } else {
      // Scroll to the specified section
      const targetSection = document.getElementById(dynamicNavTarget);
      if (targetSection) {
        targetSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };
  
  // Custom close handler with animation
  const handleClose = () => {
    setIsClosing(true);
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      onClose();
    }, 300); // Match animation duration
  };
  
  // Create the notification content
  const notificationContent = (
    <>
      <style>
        {`
          @keyframes slideDown {
            0% { transform: translateY(-100%); }
            100% { transform: translateY(0); }
          }
          @keyframes slideUp {
            0% { transform: translateY(0); }
            100% { transform: translateY(-100%); }
          }
          .notification-slide-down {
            animation: slideDown 0.3s ease-out forwards;
          }
          .notification-slide-up {
            animation: slideUp 0.3s ease-out forwards;
          }
          .notification-container {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            z-index: 50;
            padding: 0.75rem 1rem;
            background-color: #3966C3;
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
            border-bottom-left-radius: 12px;
            border-bottom-right-radius: 12px;
          }
          .notification-content {
            position: relative;
            max-width: 1200px;
            margin: 0 auto;
            padding: 0.75rem 1.5rem;
            background-color: rgba(255, 255, 255, 0.95);
            border-radius: 10px;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
          }
          .notification-header {
            display: flex;
            align-items: center;
            margin-bottom: 0.5rem;
          }
          .notification-icon {
            width: 28px;
            height: 28px;
            margin-right: 0.75rem;
            color: #3966C3;
            flex-shrink: 0;
          }
          .notification-title {
            font-weight: 600;
            font-size: 1.1rem;
            color: #3966C3;
          }
          .notification-message {
            color: #333;
            margin-bottom: 1rem;
            font-size: 1.1rem;
            line-height: 1.5;
          }
          .notification-button-container {
            display: flex;
            justify-content: flex-end;
            width: 100%;
          }
          @keyframes pulse-blue {
            0% {
              box-shadow: 0 0 0 0 rgba(57, 102, 195, 0.7);
            }
            70% {
              box-shadow: 0 0 0 10px rgba(57, 102, 195, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(57, 102, 195, 0);
            }
          }
          @keyframes pulse-yellow {
            0% {
              box-shadow: 0 0 0 0 rgba(244, 214, 65, 0.7);
            }
            70% {
              box-shadow: 0 0 0 10px rgba(244, 214, 65, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(244, 214, 65, 0);
            }
          }
          .cool-btn {
            position: relative;
            transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
            overflow: hidden;
            z-index: 1;
            animation: pulse-blue 2s infinite;
            background-color: #3966C3;
            color: white;
            padding: 0.5rem 1.25rem;
            border-radius: 8px;
            font-weight: 500;
            font-size: 0.95rem;
            border: none;
            cursor: pointer;
          }
          .cool-btn:before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 0;
            height: 100%;
            background-color: #F4D641;
            transition: width 0.3s ease;
            z-index: -1;
          }
          .cool-btn:hover {
            color: #333333;
            transform: translateY(-3px) scale(1.05);
            box-shadow: 0 7px 14px rgba(50, 50, 93, 0.1), 0 3px 6px rgba(0, 0, 0, 0.08);
            animation: pulse-yellow 2s infinite;
          }
          .cool-btn:hover:before {
            width: 100%;
          }
          .cool-btn:active {
            transform: translateY(1px) scale(0.98);
          }
          .close-button {
            position: absolute;
            top: 0.75rem;
            right: 0.75rem;
            background: none;
            border: none;
            color: #666;
            cursor: pointer;
            padding: 0.25rem;
            transition: color 0.2s ease;
            z-index: 1;
          }
          .close-button:hover {
            color: #333;
          }
          .close-button svg {
            width: 1rem;
            height: 1rem;
          }
        `}
      </style>
      <div className={`notification-container ${isClosing ? 'notification-slide-up' : 'notification-slide-down'}`}>
        <div className="notification-content">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="close-button"
            aria-label="Close notification"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Notification header */}
          <div className="notification-header">
            <svg className="notification-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="notification-title">Notification</span>
          </div>
          
          {/* Dynamic message */}
          <p className="notification-message">
            {dynamicMessage}
          </p>
          
          {/* Action button */}
          <div className="notification-button-container">
            <button
              onClick={handleAction}
              className="cool-btn"
            >
              {buttonText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
  
  // Use React Portal to render the notification at the root level of the DOM
  return ReactDOM.createPortal(
    notificationContent,
    document.body
  );
};

export default NotificationPopup;